import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    Tooltip
} from '@mui/material';
import {
    Public as GlobalIcon,
    Home as CustomIcon,
} from '@mui/icons-material';
import ShareIcon from '@mui/icons-material/Share';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import Table from '../../../../components/Table/Table';
import { MODAL_BORDER_RADIUS } from '../../utils/dateUtils';

// Helper function to calculate date from month-day pattern
const calculateDateFromMonthDay = (startDate, relativeMonth, dayOfMonth) => {
    if (!startDate) return null;

    try {
        const result = new Date(startDate);

        // Add months (relativeMonth is 1-based, so subtract 1)
        result.setMonth(result.getMonth() + (relativeMonth - 1));

        // Check if the day exists in this month
        const daysInMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();

        // Adjust for month end if needed
        const targetDay = dayOfMonth > daysInMonth ? daysInMonth : dayOfMonth;

        result.setDate(targetDay);

        return result;
    } catch (error) {
        console.error("Error calculating date:", error);
        return null;
    }
};

// Helper function to get display name for content
const getContentDisplayName = (item) => {
    if (item.contentType === 'RandomSimulation') {
        return item.contentName || 'Random Simulation Collection';
    }
    return item.contentName || item.contentId;
};

// Helper function to get display content type
const getDisplayContentType = (contentType) => {
    switch (contentType) {
        case 'RandomSimulation':
            return 'Random Simulation';
        case 'Simulation':
            return 'Simulation';
        case 'Course':
            return 'Course';
        case 'ContentPack':
            return 'Content Pack';
        default:
            return contentType;
    }
};

// Helper function to get chip color for content type
const getContentTypeColor = (contentType) => {
    switch (contentType) {
        case 'Simulation':
        case 'RandomSimulation':
            return 'primary';
        case 'Course':
        case 'ContentPack':
            return 'warning';
        default:
            return 'default';
    }
};

const PresetPreviewDialog = ({ open, onClose, preset, onEdit }) => {
    const [startDate, setStartDate] = useState('');
    const [minStartDate, setMinStartDate] = useState('');
    const [itemsPaginationModel, setItemsPaginationModel] = useState({
        pageSize: 20,
        page: 0
    });

    // Set minimum start date when component mounts
    useEffect(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setMinStartDate(tomorrow.toISOString().split('T')[0]);
    }, []);

    // Reset state when dialog opens with a new preset
    useEffect(() => {
        if (open) {
            setStartDate('');
            setItemsPaginationModel({ pageSize: 20, page: 0 });
        }
    }, [open]);

    // Validate if start date is valid (must be in the future)
    const isStartDateValid = startDate && new Date(startDate) > new Date(new Date().setHours(0, 0, 0, 0));

    // Format date as string
    const formatDate = (date) => {
        if (!date) return "";
        return date.toLocaleDateString();
    };

    const itemsHeadCells = [
        {
            id: 'relativeMonth',
            label: 'Month',
            initialVisible: true,
            render: (row) => (
                <Chip
                    label={`Month ${row.relativeMonth}`}
                    color="default"
                    variant="outlined"
                    size="small"
                />
            )
        },
        {
            id: 'dayOfMonth',
            label: 'Day',
            initialVisible: true,
            render: (row) => (
                <Chip
                    label={`Day ${row.dayOfMonth}`}
                    color="secondary"
                    variant="outlined"
                    size="small"
                />
            )
        },
        {
            id: 'contentType',
            label: 'Content Type',
            initialVisible: true,
            render: (row) => (
                <Chip
                    label={getDisplayContentType(row.contentType)}
                    color={getContentTypeColor(row.contentType)}
                    size="small"
                />
            )
        },
        {
            id: 'contentId',
            label: 'Content Name',
            initialVisible: true,
            render: (row) => getContentDisplayName(row)
        },
        {
            id: 'delivery',
            label: 'Delivery',
            initialVisible: true,
            render: (row) => {
                if (row.isBatchSend) {
                    return (
                        <Tooltip title={
                            <Box>
                                <Typography variant="caption" display="block">
                                    Duration: {row.batchDurationDays || 'N/A'} days
                                </Typography>
                                <Typography variant="caption" display="block">
                                    Time: {row.sendingTimeStart || '00:00'} - {row.sendingTimeEnd || '23:59'}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    Pause weekends: {row.pauseWeekends ? 'Yes' : 'No'}
                                </Typography>
                            </Box>
                        }>
                            <Chip
                                label="Batch"
                                color="secondary"
                                variant="outlined"
                                size="small"
                            />
                        </Tooltip>
                    );
                }
                return (
                    <Chip
                        label="Direct"
                        color="default"
                        variant="outlined"
                        size="small"
                    />
                );
            }
        },
        {
            id: 'scheduledDate',
            label: 'Scheduled Date',
            initialVisible: true,
            render: (row) => {
                if (!startDate) return "-";

                try {
                    // Calculate the scheduled date using month-day pattern
                    const scheduledDate = calculateDateFromMonthDay(
                        new Date(startDate),
                        row.relativeMonth,
                        row.dayOfMonth
                    );
                    return formatDate(scheduledDate);
                } catch (error) {
                    console.error("Error calculating scheduled date:", error);
                    return "-";
                }
            }
        }
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    width: '80%',
                    height: '90vh',
                    maxWidth: '1200px',
                    maxHeight: '1000px',
                    bgcolor: 'background.default',
                    borderRadius: MODAL_BORDER_RADIUS
                }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h6">
                            {preset?.name}
                            {' '}
                            <Chip
                                icon={preset?.sharedTenancyIds && preset.sharedTenancyIds.includes(0) ? <GlobalIcon /> : <CustomIcon />}
                                label={preset?.sharedTenancyIds && preset.sharedTenancyIds.includes(0) ? "Global Template" : "Custom Template"}
                                color={preset?.sharedTenancyIds && preset.sharedTenancyIds.includes(0) ? "primary" : "default"}
                                size="small"
                                sx={{ ml: 1 }}
                            />
                        </Typography>

                        {/* Sharing info chips */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {preset?.publishToFamily && (
                                <Chip
                                    icon={<FamilyRestroomIcon />}
                                    label="Shared with Tenancy Family"
                                    color="secondary"
                                    size="small"
                                />
                            )}
                            {preset?.sharedTenancyIds && preset.sharedTenancyIds.length > 0 &&
                                !preset.sharedTenancyIds.includes(0) && (
                                    <Chip
                                        icon={<ShareIcon />}
                                        label={`Shared with ${preset.sharedTenancyIds.length} ${preset.sharedTenancyIds.length === 1 ? 'tenancy' : 'tenancies'
                                            }`}
                                        color="info"
                                        size="small"
                                    />
                                )}
                        </Box>
                    </Box>
                    {onEdit && (
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => onEdit(preset)}
                        >
                            Edit Template
                        </Button>
                    )}
                </Box>
            </DialogTitle>
            <DialogContent sx={{ overflowY: 'auto' }}>
                {preset && (
                    <>
                        {/* Title and date picker in one row */}
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            justifyContent: 'space-between',
                            mt: 1,
                            mb: 1
                        }}>
                            <Typography variant="h6" sx={{ mb: { xs: 2, sm: 0 } }}>
                                Content Items ({preset.items?.length || 0})
                            </Typography>

                            <Box sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                flexDirection: { xs: 'column', sm: 'row' }
                            }}>
                                <TextField
                                    label="Start Date"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    inputProps={{
                                        min: minStartDate,
                                    }}
                                    error={!!startDate && !isStartDateValid}
                                    helperText={startDate && !isStartDateValid ? "Please select a future date" : ""}
                                    sx={{
                                        width: '200px'
                                    }}
                                    size="small"
                                />
                                {startDate && isStartDateValid && (
                                    <Button
                                        variant="outlined"
                                        size="medium"
                                        onClick={() => setStartDate('')}
                                        sx={{
                                            ml: { xs: 0, sm: 1 },
                                            mt: { xs: 1, sm: 0 },
                                        }}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </Box>
                        </Box>

                        {/* Date explanation */}
                        {!startDate && (
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Select a start date to see when each item would be scheduled.
                                </Typography>
                            </Box>
                        )}

                        {/* Items table */}
                        <Table
                            name=""
                            data={preset.items || []}
                            error={null}
                            loading={false}
                            headCells={itemsHeadCells}
                            search={false}
                            selected={[]}
                            setSelected={() => { }}
                            checkbox={false}
                            toolbarActions={[]}
                            showToolbarActions={false}
                            maxTableHeight="535px"
                            paginationModel={itemsPaginationModel}
                            setPaginationModel={setItemsPaginationModel}
                            rowCount={preset.items?.length || 0}
                            disablePagination={false}
                            initialVisibleColumns={itemsHeadCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
                            size="small"
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onClose}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

PresetPreviewDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    preset: PropTypes.object,
    onEdit: PropTypes.func
};

export default PresetPreviewDialog;
